import React, { useMemo, ReactDOM } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, Button, Container, CssBaseline, Toolbar, Typography, useMediaQuery } from '@mui/material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';

// assets
import { IconChevronRight } from '@tabler/icons';

import bgImg from 'assets/images/bg-img.png';
import LogoSection from './LogoSection';
import notFoundImage from 'assets/images/pageNotFound/404-logo.png';
import projectLogo from 'assets/images/project-logo.png';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    ...theme.typography.mainContent,
    ...(!open && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: -(drawerWidth - 20),
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginRight: '10px'
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter
        }),
        marginLeft: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px'
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const location = useLocation();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('lg'));

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { container } = useConfig();

    const regex = /^[/]w[/][0-9a-zA-Z-]*[/]p[/][0-9a-zA-Z-]*[/]c-d[/][0-9a-zA-Z-]*/gi;

    const [limitedHeight, setLimitedHeight] = React.useState(true);

    React.useEffect(() => {
        setLimitedHeight(true);
        if (regex.test(location.pathname)) {
            setLimitedHeight(false);
        }
    }, [location]);

    React.useEffect(() => {
        dispatch(openDrawer(!matchDownMd));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownMd]);

    const header = useMemo(
        () => (
            <Toolbar>
                <Header />
            </Toolbar>
        ),
        []
    );

    return (
        <Box
            sx={{
                display: 'flex',
                backgroundImage: `url(${bgImg})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                height: '100vh',
                maxHeight: '100vh'
            }}
        >
            <CssBaseline />
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: 'transparent',
                    transition: drawerOpen ? theme.transitions.create('width') : 'none'
                }}
            >
                <Box sx={{ width: '260px', display: 'flex', justifyContent: 'center', padding: '2rem 0' }} onClick={() => navigate('/')}>
                    <Box component="img" alt="Nectar Icon" src={projectLogo} sx={{ width: '200px', height: 'auto' }} />
                </Box>
            </AppBar>

            {/* main content */}
            <Main
                theme={theme}
                open={drawerOpen}
                sx={{
                    height: '100vh',
                    minHeight: 'unset',
                    bgcolor: 'transparent'
                }}
            >
                {/* breadcrumb */}
                <Outlet />
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 74.52px)' }}>
                    <Box component="img" alt="page not found logo" src={notFoundImage} sx={{ marginBottom: '32px' }} />
                    <Typography sx={{ fontSize: '36px', fontWeight: 400, lineHeight: '50.4px', color: '#5A1C70', marginBottom: '32px' }}>
                        <FormattedMessage id="page-not-found-string-one" />
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '22.4px', color: '#5A1C70', marginBottom: '32px' }}>
                        <FormattedMessage id="page-not-found-string-two" />
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        sx={{
                            backgroundColor: '#7A2897',
                            color: '#FFFFFF',
                            borderRadius: '16px',
                            fontWeight: 700,
                            '&:hover': {
                                backgroundColor: '#7A2897',
                                color: '#FFFFFF'
                            }
                        }}
                        onClick={() => {
                            navigate('/')
                        }}
                    >
                        <FormattedMessage id="page-not-found-button-back" />
                    </Button>
                </Box>
            </Main>
        </Box>
    );
};

export default MainLayout;
